import AppearanceSwitcher from '@/components/layout/AppearanceSwitcher';
import { UserInfo } from '@/components/layout/UserInfo';
import { Badge } from '@/components/ui/shadcn/badge';
import { DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/shadcn/dropdown-menu';
import { useMobileNavigation } from '@/hooks/use-mobile-navigation';
import { useTranslations } from '@/hooks/useTranslations';
import { type SharedData, type User } from '@/types';
import { Link, router, usePage } from '@inertiajs/react';
import { LogOut, Settings } from 'lucide-react';
import { ImitateButton } from './ImitateButton';

interface UserMenuContentProps {
    user: User;
}

export function UserMenuContent({ user }: UserMenuContentProps) {
    const cleanup = useMobileNavigation();
    const { t } = useTranslations();
    const { locale, locales } = usePage<SharedData>().props;

    const handleLocaleChange = (nextLocale: string) => {
        if (!nextLocale || nextLocale === locale) {
            return;
        }

        router.patch(
            route('profile.locale.update'),
            { locale: nextLocale },
            {
                preserveScroll: true,
                preserveState: true,
            },
        );
    };

    return (
        <>
            <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex flex-col gap-1.5 px-1 py-1.5 text-left text-sm">
                    <div className="flex items-center gap-2">
                        <UserInfo user={user} showEmail={true} />
                    </div>
                    {user.role && (
                        <Badge variant="secondary" className="w-fit text-xs font-normal">
                            {user.role}
                        </Badge>
                    )}
                </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
                <div className="px-2 py-1.5">
                    <AppearanceSwitcher />
                    {locales && locales.length > 0 && (
                        <div className="mt-3 space-y-1">
                            <p className="text-muted-foreground text-xs font-medium">{t('settings.profile.language', 'Language')}</p>
                            <div className="flex gap-1">
                                {locales.map((code) => {
                                    const isActive = code === locale;
                                    const label =
                                        code === 'en'
                                            ? t('settings.profile.language_en', 'English')
                                            : code === 'lt'
                                              ? t('settings.profile.language_lt', 'Lietuvių')
                                              : code.toUpperCase();

                                    return (
                                        <button
                                            key={code}
                                            type="button"
                                            onClick={() => handleLocaleChange(code)}
                                            className={[
                                                'inline-flex flex-1 items-center justify-center rounded-md border px-2 py-1 text-xs transition-colors',
                                                isActive
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
                                            ].join(' ')}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div className="mt-3">
                        <ImitateButton isAdministrator={user.role === 'Administrator'} className="w-full" />
                    </div>
                </div>
                <DropdownMenuItem asChild>
                    <Link className="block w-full" href={route('profile.edit')} as="button" prefetch onClick={cleanup}>
                        <Settings className="mr-2" />
                        {t('navigation.settings', 'Settings')}
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
                <Link className="block w-full" method="post" href={route('logout')} as="button" onClick={cleanup}>
                    <LogOut className="mr-2" />
                    {t('navigation.logout', 'Log out')}
                </Link>
            </DropdownMenuItem>
        </>
    );
}
